'use client';
import React, { useState } from 'react';
import { GitBranch, User, Building2, Calendar, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface OwnershipRecord {
  id: string;
  owner: string;
  ownerType: 'individual' | 'company' | 'government';
  from: string;
  to: string | null;
  transferType: 'purchase' | 'inheritance' | 'grant' | 'dispute';
  documentRef: string;
  verified: boolean;
  notes?: string;
}

const OWNERSHIP_DATA: Record<string, { parcel: string; address: string; county: string; chain: OwnershipRecord[] }> = {
  'LR-MON-0042': {
    parcel: 'LR-MON-0042',
    address: '14 Broad St, Monrovia',
    county: 'Montserrado',
    chain: [
      { id: 'oc-1', owner: 'Liberian Government', ownerType: 'government', from: '1960', to: '1988', transferType: 'grant', documentRef: 'GOV-DEED-1960-0042', verified: true },
      { id: 'oc-2', owner: 'James K. Kollie (Sr.)', ownerType: 'individual', from: '1988', to: '2005', transferType: 'purchase', documentRef: 'DEED-1988-MON-0042', verified: true },
      { id: 'oc-3', owner: 'Kollie Family Estate', ownerType: 'individual', from: '2005', to: '2018', transferType: 'inheritance', documentRef: 'PROBATE-2005-0042', verified: true, notes: 'Transferred upon death of James K. Kollie Sr.' },
      { id: 'oc-4', owner: 'Kollie Trading Co.', ownerType: 'company', from: '2018', to: null, transferType: 'purchase', documentRef: 'CORP-DEED-2018-0042', verified: true, notes: 'Current registered owner. Tax compliant.' },
    ],
  },
  'LR-LOF-0019': {
    parcel: 'LR-LOF-0019',
    address: 'Voinjama Riverside',
    county: 'Lofa',
    chain: [
      { id: 'oc-1', owner: 'Lofa County Authority', ownerType: 'government', from: '1975', to: '2001', transferType: 'grant', documentRef: 'LOFA-GRANT-1975-019', verified: true },
      { id: 'oc-2', owner: 'Emmanuel Sumo', ownerType: 'individual', from: '2001', to: '2019', transferType: 'purchase', documentRef: 'DEED-2001-LOF-019', verified: true },
      { id: 'oc-3', owner: 'Contested — Sumo Family vs. Kamara Clan', ownerType: 'individual', from: '2019', to: null, transferType: 'dispute', documentRef: 'DISPUTE-2019-LOF-019', verified: false, notes: 'Active legal dispute. Two competing claims filed. Court case pending.' },
    ],
  },
  'LR-NIM-0008': {
    parcel: 'LR-NIM-0008',
    address: 'Ganta Market Zone B',
    county: 'Nimba',
    chain: [
      { id: 'oc-1', owner: 'Unknown — No Records Found', ownerType: 'individual', from: '?', to: null, transferType: 'dispute', documentRef: 'NONE', verified: false, notes: 'AI-detected unregistered structure. No ownership records exist in national registry.' },
    ],
  },
};

const DEFAULT_PARCEL = 'LR-MON-0042';

const transferColors = {
  purchase: { color: '#34d399', label: 'Purchase' },
  inheritance: { color: '#60a5fa', label: 'Inheritance' },
  grant: { color: 'var(--accent)', label: 'Gov. Grant' },
  dispute: { color: '#f87171', label: 'Disputed' },
};

const ownerIcon = {
  individual: <User size={14} />,
  company: <Building2 size={14} />,
  government: <Building2 size={14} />,
};

interface OwnershipChainPanelProps {
  parcelId: string | null;
}

export default function OwnershipChainPanel({ parcelId }: OwnershipChainPanelProps) {
  const [selectedId, setSelectedId] = useState(parcelId || DEFAULT_PARCEL);
  const data = OWNERSHIP_DATA[selectedId] || OWNERSHIP_DATA[DEFAULT_PARCEL];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Parcel selector */}
      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-center gap-2 mb-4">
          <GitBranch size={15} style={{ color: 'var(--accent)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Select Parcel</span>
        </div>
        <div className="space-y-2">
          {Object.keys(OWNERSHIP_DATA).map((id) => {
            const d = OWNERSHIP_DATA[id];
            const isSelected = selectedId === id;
            const hasDispute = d.chain.some((c) => c.transferType === 'dispute');
            return (
              <button
                key={id}
                onClick={() => setSelectedId(id)}
                className="w-full text-left p-3 rounded-lg transition-all duration-150"
                style={{
                  backgroundColor: isSelected ? 'rgba(212,160,23,0.1)' : 'var(--dark-elevated)',
                  border: `1px solid ${isSelected ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{id}</span>
                  {hasDispute && <AlertTriangle size={12} style={{ color: '#f87171' }} />}
                </div>
                <p className="text-xs truncate" style={{ color: 'var(--dark-text)' }}>{d.address}</p>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{d.county}</p>
              </button>
            );
          })}
        </div>

        {/* Summary stats */}
        <div className="mt-4 pt-4 border-t space-y-2" style={{ borderColor: 'var(--dark-border)' }}>
          <p className="text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>Chain Summary</p>
          <div className="flex justify-between text-xs">
            <span style={{ color: 'var(--dark-muted)' }}>Total transfers</span>
            <span className="font-mono-data font-bold" style={{ color: 'var(--dark-text)' }}>{data.chain.length}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span style={{ color: 'var(--dark-muted)' }}>Verified records</span>
            <span className="font-mono-data font-bold" style={{ color: '#34d399' }}>{data.chain.filter((c) => c.verified).length}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span style={{ color: 'var(--dark-muted)' }}>Disputes</span>
            <span className="font-mono-data font-bold" style={{ color: '#f87171' }}>{data.chain.filter((c) => c.transferType === 'dispute').length}</span>
          </div>
        </div>
      </div>

      {/* Chain visualization */}
      <div className="lg:col-span-2 rounded-xl p-5" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-center gap-2 mb-5">
          <GitBranch size={16} style={{ color: 'var(--accent)' }} />
          <div>
            <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Ownership Chain — </span>
            <span className="text-sm font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{data.parcel}</span>
          </div>
        </div>
        <p className="text-xs mb-5" style={{ color: 'var(--dark-muted)' }}>{data.address} · {data.county}</p>

        {/* Chain nodes */}
        <div className="relative">
          {data.chain.map((record, idx) => {
            const tc = transferColors[record.transferType];
            const isLast = idx === data.chain.length - 1;
            return (
              <div key={record.id} className="flex gap-4 mb-0">
                {/* Timeline line */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                    style={{ backgroundColor: record.verified ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)', border: `2px solid ${record.verified ? '#34d399' : '#f87171'}`, color: record.verified ? '#34d399' : '#f87171' }}
                  >
                    {ownerIcon[record.ownerType]}
                  </div>
                  {!isLast && (
                    <div className="w-0.5 flex-1 my-1" style={{ backgroundColor: 'var(--dark-border)', minHeight: '32px' }} />
                  )}
                </div>

                {/* Record card */}
                <div
                  className="flex-1 p-3 rounded-lg mb-3"
                  style={{ backgroundColor: 'var(--dark-elevated)', border: `1px solid ${record.transferType === 'dispute' ? 'rgba(248,113,113,0.3)' : 'var(--dark-border)'}` }}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: record.transferType === 'dispute' ? '#f87171' : 'var(--dark-text)' }}>
                        {record.owner}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1">
                          <Calendar size={11} style={{ color: 'var(--dark-muted)' }} />
                          <span className="text-xs font-mono-data" style={{ color: 'var(--dark-muted)' }}>
                            {record.from} — {record.to ?? 'Present'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: `${tc.color}20`, color: tc.color }}>
                        {tc.label}
                      </span>
                      {record.verified
                        ? <CheckCircle2 size={14} style={{ color: '#34d399' }} />
                        : <AlertTriangle size={14} style={{ color: '#f87171' }} />
                      }
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 mb-1">
                    <FileText size={11} style={{ color: 'var(--dark-muted)' }} />
                    <span className="text-xs font-mono-data" style={{ color: 'var(--dark-muted)' }}>{record.documentRef}</span>
                  </div>

                  {record.notes && (
                    <p className="text-xs mt-1.5 p-2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.03)', color: 'var(--dark-muted)', borderLeft: `2px solid ${tc.color}` }}>
                      {record.notes}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
